import { DragHandle } from '@mui/icons-material'
import { TableBody, TableRow, TableCell, Theme } from '@mui/material'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult
} from 'react-beautiful-dnd'
import { Dispatch, ReactNode, SetStateAction, CSSProperties } from 'react'
import { ID } from '../types'

interface DraggableStyleProps {
  top?: number | string
  [key: string]: any
}

/**
 * Get the style for a draggable item
 */
export const getItemStyle = (
  snapshot: DraggableStateSnapshot,
  draggableStyle: DraggableStyleProps,
  bg: string = '#fff'
): CSSProperties => {
  return {
    ...draggableStyle,
    background: bg,
    display: snapshot.isDragging ? 'table' : '',
    left: 'auto !important',
    top: draggableStyle.top,
    boxShadow: snapshot.isDragging ? '0 5px 18px 5px rgba(0, 0, 0, 0.25)' : ''
  }
}

interface DroppableProps {
  children: ReactNode
}

/**
 * Creates a droppable component with drag and drop functionality
 */
export const DroppableComponent = (
  onDragEnd: (result: DropResult) => void,
  additionnalRow?: ReactNode
) => {
  return function DroppableWithProps({ children }: DroppableProps) {
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId={'opportunities-list-array'}
          direction="vertical"
        >
          {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
            return (
              <TableBody
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  backgroundColor: snapshot.isDraggingOver
                    ? 'lightgrey'
                    : '#fff'
                }}
              >
                {children}
                {provided.placeholder}
                {additionnalRow}
              </TableBody>
            )
          }}
        </Droppable>
      </DragDropContext>
    )
  }
}

interface DraggableProps {
  children: ReactNode
}

/**
 * Creates a draggable component with drag handle
 */
export const DraggableComponent = (
  id: string,
  index: number,
  className: string,
  bg: string = '#fff',
  dragHandleVisible: boolean = true,
  dragHandleDisabled: boolean = false
) => {
  return function DraggableWithProps({ children }: DraggableProps) {
    return (
      <Draggable
        draggableId={id}
        index={index}
        isDragDisabled={dragHandleDisabled}
      >
        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
          return (
            <TableRow
              ref={provided.innerRef}
              {...provided.draggableProps}
              style={getItemStyle(snapshot, provided.draggableProps.style, bg)}
              className={className}
              hover={false}
            >
              {dragHandleVisible && (
                <TableCell
                  {...provided.dragHandleProps}
                  sx={{ width: (t: Theme) => t.spacing(3) }}
                >
                  <DragHandle
                    color={dragHandleDisabled ? 'disabled' : 'primary'}
                  />
                </TableCell>
              )}
              {children}
            </TableRow>
          )
        }}
      </Draggable>
    )
  }
}

interface DraggableItem {
  order: number
  [key: string]: any
}

/**
 * Handle the end of a drag operation
 */
export const onDragEnd = async <T extends DraggableItem>(
  result: DropResult,
  items: T[],
  setItems: Dispatch<SetStateAction<T[]>>,
  patchFct: (
    id: string,
    data: { order: number; position: 'above' | 'below' }
  ) => Promise<any>,
  mutate: (() => void) | null = null
): Promise<void> => {
  if (!result.destination) return

  const backupList = [...items]
  const listCopy = [...items]

  const newOrderId = listCopy[result.destination.index].order
  const oldOrderId = listCopy[result.source.index].order

  const [removedElement] = listCopy.splice(result.source.index, 1)
  listCopy.splice(result.destination.index, 0, removedElement)

  setItems(listCopy)

  const response = await patchFct(result.draggableId, {
    order: newOrderId,
    position: newOrderId > oldOrderId ? 'below' : 'above'
  })

  if (!response || (response?.status !== 200 && response?.status !== 201)) {
    setItems(backupList)
    return
  }

  if (mutate) {
    mutate()
  }
}
